import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Logo, LogoLight } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { api } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, ShieldCheck, CheckCircle2, Eye, EyeOff,
  User, MapPin, Lock, Phone, Award, Globe2, Sparkles,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { COUNTRIES, flagEmoji } from '../lib/countries';
import { useLanguage } from '../lib/LanguageContext';

const initial = {
  civilite: 'M.', nom: '', prenom: '',
  date_naissance: '', lieu_naissance: '', pays_naissance: 'FR',
  nationalite: 'Française',
  adresse_numero: '', adresse_rue: '', adresse_code_postal: '',
  adresse_ville: '', adresse_pays: 'France', adresse_pays_code: 'FR',
  telephone: '', email: '',
  password: '', confirm_password: '',
  accept_cgu: false, accept_confidentialite: false,
};

function pwdStrength(p, t) {
  let n = 0;
  if (!p) return { score: 0, label: '', color: 'bg-slate-200', strict: false };
  if (p.length >= 8) n++;
  if (/[A-Z]/.test(p)) n++;
  if (/[a-z]/.test(p)) n++;
  if (/\d/.test(p)) n++;
  if (/[^A-Za-z0-9]/.test(p)) n++;
  const map = [
    { label: t('auth.pwVeryWeak'), color: 'bg-rose-500' },
    { label: t('auth.pwWeak'), color: 'bg-rose-400' },
    { label: t('auth.pwMedium'), color: 'bg-amber-400' },
    { label: t('auth.pwGood'), color: 'bg-blue-500' },
    { label: t('auth.pwStrong'), color: 'bg-emerald-600' },
    { label: t('auth.pwVeryStrong'), color: 'bg-emerald-700' },
  ];
  return { score: n, label: map[n].label, color: map[n].color, strict: n >= 5 };
}

export default function Inscription() {
  const { t } = useLanguage();
  const STEPS = [
    { id: 1, label: t('auth.identityStep'), icon: User, desc: t('auth.identityStepDesc') },
    { id: 2, label: t('auth.contactStep'), icon: MapPin, desc: t('auth.contactStepDesc') },
    { id: 3, label: t('auth.securityStep'), icon: Lock, desc: t('auth.securityStepDesc') },
  ];
  const [form, setForm] = useState(initial);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const navigate = useNavigate();

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setNationality = (code) => {
    const c = COUNTRIES.find((x) => x.code === code);
    upd('nationalite', c ? c.nationality : '');
  };
  const setAddressCountry = (code) => {
    const c = COUNTRIES.find((x) => x.code === code);
    setForm((f) => ({ ...f, adresse_pays_code: code, adresse_pays: c ? c.name : '' }));
  };
  const setBirthCountry = (code) => {
    upd('pays_naissance', code);
  };

  const validateStep = (s) => {
    if (s === 1) return form.civilite && form.nom && form.prenom && form.date_naissance && form.lieu_naissance && form.nationalite;
    if (s === 2) return form.adresse_numero && form.adresse_rue && form.adresse_code_postal && form.adresse_ville && form.adresse_pays && form.telephone;
    if (s === 3) {
      if (!form.email || !form.password || form.password !== form.confirm_password) return false;
      const ps = pwdStrength(form.password, t);
      return ps.strict && form.accept_cgu && form.accept_confidentialite;
    }
    return true;
  };

  const submit = async () => {
    if (!validateStep(3)) {
      toast.error(t('auth.fixErrors'));
      return;
    }
    setLoading(true);
    try {
      // Backend expects flat fields - we send the canonical name
      await api.post('/auth/register', {
        civilite: form.civilite,
        nom: form.nom, prenom: form.prenom,
        date_naissance: form.date_naissance,
        lieu_naissance: form.lieu_naissance,
        nationalite: form.nationalite,
        adresse_numero: form.adresse_numero, adresse_rue: form.adresse_rue,
        adresse_code_postal: form.adresse_code_postal,
        adresse_ville: form.adresse_ville, adresse_pays: form.adresse_pays,
        telephone: form.telephone, email: form.email,
        password: form.password, confirm_password: form.confirm_password,
        accept_cgu: form.accept_cgu, accept_confidentialite: form.accept_confidentialite,
      });
      setSuccess(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('auth.signupError'));
    } finally {
      setLoading(false);
    }
  };

  const ps = pwdStrength(form.password, t);

  return (
    <div className="min-h-screen grid lg:grid-cols-5 bg-slate-50">
      {/* LEFT - Brand panel */}
      <div className="hidden lg:block lg:col-span-2 relative overflow-hidden">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(160deg, #0b1538 0%, #1e3a8a 50%, #1e40af 100%)' }}></div>
        <div className="absolute -top-40 -right-40 w-[480px] h-[480px] bg-blue-500/25 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-[480px] h-[480px] bg-indigo-500/30 rounded-full blur-3xl"></div>

        <div className="relative h-full flex flex-col justify-between p-10 xl:p-14 text-white">
          <Link to="/"><LogoLight size={42} /></Link>

          <div className="max-w-md">
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-xs font-medium mb-6">
              <Sparkles className="w-3 h-3"/> {t('auth.signupBadge')}
            </div>
            <h2 className="text-3xl xl:text-4xl font-bold leading-tight tracking-tight">
              {t('auth.signupPanelTitle1')}<br/>
              <span className="bg-gradient-to-r from-blue-200 to-indigo-200 bg-clip-text text-transparent">{t('auth.signupPanelTitle2')}</span>
            </h2>
            <p className="text-blue-100/90 mt-5 text-[15px] leading-relaxed">
              {t('auth.signupPanelDesc')}
            </p>

            {/* Stepper sidebar */}
            <div className="mt-10 space-y-3">
              {STEPS.map((s) => {
                const done = step > s.id;
                const current = step === s.id;
                return (
                  <div key={s.id} className={`flex gap-3 items-start p-3 rounded-xl border ${current ? 'bg-white/10 border-white/30' : done ? 'bg-emerald-500/15 border-emerald-400/30' : 'bg-white/5 border-white/10'}`}>
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${current ? 'bg-white text-blue-900' : done ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white'}`}>
                      {done ? <CheckCircle2 className="w-5 h-5"/> : <s.icon className="w-5 h-5"/>}
                    </div>
                    <div>
                      <div className="text-sm font-semibold">{s.label}</div>
                      <div className="text-xs text-blue-100/70">{s.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="text-xs text-blue-200/70 flex flex-wrap gap-x-4 gap-y-1">
            <div className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5"/> ACPR agréé</div>
            <div className="flex items-center gap-1"><Award className="w-3.5 h-3.5"/> FGDR 100k€</div>
            <div className="flex items-center gap-1"><Globe2 className="w-3.5 h-3.5"/> SEPA + SWIFT</div>
          </div>
        </div>
      </div>

      {/* RIGHT - Form */}
      <div className="lg:col-span-3 flex flex-col bg-white">
        <div className="border-b border-slate-100">
          <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
            <Link to="/" className="text-sm text-slate-500 hover:text-blue-800 inline-flex items-center gap-1.5">
              <ArrowLeft className="w-4 h-4"/> {t('common.back')}
            </Link>
            <Link to="/connexion" className="text-sm text-slate-600 hover:text-blue-800">{t('auth.alreadyClient')} <span className="font-semibold text-blue-700">{t('auth.loginAction')}</span></Link>
          </div>
        </div>

        <div className="flex-1 py-8 px-4">
          <div className="max-w-2xl mx-auto">
            {/* Mobile header */}
            <div className="lg:hidden mb-6"><Logo /></div>

            <div className="text-xs font-semibold text-blue-700 uppercase tracking-widest">{t('auth.signupTitle')}</div>
            <h1 className="text-3xl md:text-4xl font-bold mt-1 tracking-tight">{t('auth.signupTitle')}</h1>
            <p className="text-slate-600 mt-2">{t('auth.signupSubtitle2')}</p>

            {/* Progress */}
            <div className="mt-6 flex items-center gap-2">
              {STEPS.map((s) => (
                <div key={s.id} className={`flex-1 h-1.5 rounded-full ${step >= s.id ? 'bg-blue-700' : 'bg-slate-200'}`}/>
              ))}
            </div>
            <div className="text-xs text-slate-500 mt-2">{t('auth.stepXof3', { step })} {STEPS[step - 1].label}</div>

            <div className="mt-6 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-6 md:p-8">
                {step === 1 && <div className="space-y-5">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label>{t('auth.civilite')}</Label>
                      <Select value={form.civilite} onValueChange={(v) => upd('civilite', v)}>
                        <SelectTrigger data-testid="input-civilite"><SelectValue/></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="M.">{t('auth.civilityMr')}</SelectItem>
                          <SelectItem value="Mme">{t('auth.civilityMme')}</SelectItem>
                          <SelectItem value="Autre">{t('auth.civiliteOther')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div><Label>{t('common.firstName')}</Label><Input value={form.prenom} onChange={(e) => upd('prenom', e.target.value)} data-testid="input-prenom"/></div>
                    <div><Label>{t('common.name')}</Label><Input value={form.nom} onChange={(e) => upd('nom', e.target.value)} data-testid="input-nom"/></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('auth.birthDate')}</Label>
                      <Input type="date" value={form.date_naissance} onChange={(e) => upd('date_naissance', e.target.value)} data-testid="input-date_naissance"/>
                    </div>
                    <div>
                      <Label>{t('auth.birthCountry')}</Label>
                      <Select value={form.pays_naissance} onValueChange={setBirthCountry}>
                        <SelectTrigger data-testid="select-pays-naissance"><SelectValue/></SelectTrigger>
                        <SelectContent className="max-h-72">
                          {COUNTRIES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              <span className="mr-2">{flagEmoji(c.code)}</span> {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>{t('auth.birthCity')}</Label><Input value={form.lieu_naissance} onChange={(e) => upd('lieu_naissance', e.target.value)} placeholder="Paris" data-testid="input-lieu_naissance"/></div>
                    <div>
                      <Label>{t('auth.nationality')}</Label>
                      <Select
                        value={COUNTRIES.find((c) => c.nationality === form.nationalite)?.code || ''}
                        onValueChange={setNationality}
                      >
                        <SelectTrigger data-testid="select-nationalite"><SelectValue placeholder={t('auth.selectPlaceholder')}/></SelectTrigger>
                        <SelectContent className="max-h-72">
                          {COUNTRIES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              <span className="mr-2">{flagEmoji(c.code)}</span> {c.nationality}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>}

                {step === 2 && <div className="space-y-5">
                  <div className="grid grid-cols-4 gap-4">
                    <div><Label>{t('auth.streetNumber')}</Label><Input value={form.adresse_numero} onChange={(e) => upd('adresse_numero', e.target.value)} data-testid="input-adresse_numero"/></div>
                    <div className="col-span-3"><Label>{t('auth.streetName')}</Label><Input value={form.adresse_rue} onChange={(e) => upd('adresse_rue', e.target.value)} data-testid="input-adresse_rue"/></div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>{t('auth.postalCode')}</Label><Input value={form.adresse_code_postal} onChange={(e) => upd('adresse_code_postal', e.target.value)} data-testid="input-adresse_code_postal"/></div>
                    <div className="col-span-2"><Label>{t('auth.city')}</Label><Input value={form.adresse_ville} onChange={(e) => upd('adresse_ville', e.target.value)} data-testid="input-adresse_ville"/></div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{t('auth.residenceCountry')}</Label>
                      <Select value={form.adresse_pays_code} onValueChange={setAddressCountry}>
                        <SelectTrigger data-testid="select-pays-residence"><SelectValue/></SelectTrigger>
                        <SelectContent className="max-h-72">
                          {COUNTRIES.map((c) => (
                            <SelectItem key={c.code} value={c.code}>
                              <span className="mr-2">{flagEmoji(c.code)}</span> {c.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{t('auth.mobilePhone')}</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
                        <Input className="pl-9" value={form.telephone} onChange={(e) => upd('telephone', e.target.value)} placeholder="+33 6 12 34 56 78" data-testid="input-telephone"/>
                      </div>
                    </div>
                  </div>
                </div>}

                {step === 3 && <div className="space-y-5">
                  <div>
                    <Label>{t('auth.emailAddress')}</Label>
                    <Input type="email" value={form.email} onChange={(e) => upd('email', e.target.value.toLowerCase())} placeholder="prenom.nom@email.com" data-testid="input-email"/>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{t('common.password')}</Label>
                      <div className="relative">
                        <Input type={showPwd ? 'text' : 'password'} value={form.password} onChange={(e) => upd('password', e.target.value)} className="pr-10" data-testid="input-password"/>
                        <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                          {showPwd ? <EyeOff className="w-4 h-4"/> : <Eye className="w-4 h-4"/>}
                        </button>
                      </div>
                      {form.password && (
                        <div className="mt-2">
                          <div className="flex gap-1">
                            {[0, 1, 2, 3, 4].map((i) => (
                              <div key={i} className={`h-1 flex-1 rounded ${i < ps.score ? ps.color : 'bg-slate-200'}`}/>
                            ))}
                          </div>
                          <div className={`text-xs mt-1 ${ps.score >= 4 ? 'text-emerald-700' : ps.score >= 3 ? 'text-amber-600' : 'text-rose-600'}`}>{ps.label}</div>
                        </div>
                      )}
                    </div>
                    <div>
                      <Label>{t('auth.confirmPwd')}</Label>
                      <Input type={showPwd ? 'text' : 'password'} value={form.confirm_password} onChange={(e) => upd('confirm_password', e.target.value)} data-testid="input-confirm_password"/>
                      {form.confirm_password && form.password !== form.confirm_password && (
                        <div className="text-xs text-rose-600 mt-1">{t('auth.pwMismatch')}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 bg-slate-50 rounded-lg p-3">
                    {t('auth.pwdHint')}
                  </div>

                  <div className="space-y-3 pt-2">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox checked={form.accept_cgu} onCheckedChange={(v) => upd('accept_cgu', !!v)} className="mt-0.5" data-testid="input-accept_cgu"/>
                      <span className="text-sm text-slate-700">{t('auth.acceptCgu')} <Link to="/cgu" className="text-blue-700 underline" target="_blank">{t('auth.cguLink')}</Link>.</span>
                    </label>
                    <label className="flex items-start gap-3 cursor-pointer">
                      <Checkbox checked={form.accept_confidentialite} onCheckedChange={(v) => upd('accept_confidentialite', !!v)} className="mt-0.5" data-testid="input-accept_confidentialite"/>
                      <span className="text-sm text-slate-700">{t('auth.acceptPrivacy')} <Link to="/confidentialite" className="text-blue-700 underline" target="_blank">{t('auth.privacyLink')}</Link>.</span>
                    </label>
                  </div>
                </div>}
              </div>

              <div className="px-6 md:px-8 py-4 bg-slate-50 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs text-slate-500 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-emerald-600"/> {t('auth.dataEncrypted')}
                </div>
                <div className="flex gap-2">
                  {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)} data-testid="btn-prev-step">{t('common.previous')}</Button>}
                  {step < 3 && <Button className="bg-blue-700 hover:bg-blue-800" disabled={!validateStep(step)} onClick={() => setStep(step + 1)} data-testid="btn-next-step">{t('common.next')} <ArrowRight className="ml-2 w-4 h-4"/></Button>}
                  {step === 3 && <Button className="bg-blue-700 hover:bg-blue-800" disabled={!validateStep(3) || loading} onClick={submit} data-testid="btn-submit-register">{loading ? t('auth.creatingAccount') : t('auth.createMyAccount')}</Button>}
                </div>
              </div>
            </div>

            <div className="mt-6 text-center text-xs text-slate-500">
              {t('auth.dataConsent')}
            </div>
          </div>
        </div>

        <div className="hidden md:flex border-t border-slate-100 px-6 py-3 text-[11px] text-slate-400 justify-between items-center">
          <div>© {new Date().getFullYear()} BFI Groupe — {t('footer.allRightsReserved')}</div>
          <div className="flex gap-4">
            <Link to="/mentions-legales" className="hover:text-slate-700">{t('footer.mentionsLegales')}</Link>
            <Link to="/confidentialite" className="hover:text-slate-700">{t('footer.confidentialite')}</Link>
            <Link to="/cookies" className="hover:text-slate-700">{t('footer.cookies')}</Link>
          </div>
        </div>
      </div>

      <Dialog open={success} onOpenChange={setSuccess}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mb-3">
              <CheckCircle2 className="w-7 h-7"/>
            </div>
            <DialogTitle className="text-center">{t('auth.requestSaved')}</DialogTitle>
            <DialogDescription className="text-center">
              {t('auth.requestSavedDesc')}
            </DialogDescription>
          </DialogHeader>
          <Button className="bg-blue-700 hover:bg-blue-800 mt-2" onClick={() => navigate('/connexion')} data-testid="btn-success-close">{t('common.close')}</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
